export const addComponentModal = {
  extend: 'ModalWindow',
  props: {
    overflow: 'hidden',
    width: '100%',
    maxWidth: 'I3',
    onSubmit: async (e, el, s, ctx) => {
      e.preventDefault()

      const obj = s.parse()
      const { type, key } = obj

      if (!key) {
        el.call('openNotification', {
          title: el.call('polyglot', 'editor.addComponent.validation.title'),
          message: el.call('polyglot', 'editor.addComponent.keyRequired'),
          type: 'warning'
        })
        return
      }

      const exists = el.getDataItem(key, type) || el.getSchemaItem(key, type)
      if (exists) {
        el.call('openNotification', {
          title: el
            .call('polyglot', 'editor.addComponent.exists.title')
            .replace('{kind}', el.getSingular(s.type)),
          message: el
            .call('polyglot', 'editor.addComponent.exists.message')
            .replace('{key}', s.key)
            .replace('{kind}', el.getSingular(s.type)),
          type: 'error'
        })
        return
      }

      const res = await el.call('createItem', s.type, s.key, obj)
      if (res?.success) {
        el.call('closeRouterModal')
      } else {
        el.call('openNotification', {
          title: el.call('polyglot', 'editor.addComponent.error.title'),
          message: el
            .call('polyglot', 'editor.addComponent.error.message')
            .replace('{type}', s.type)
            .replace('{title}', s.title),
          type: 'error'
        })
      }
    }
  },
  tag: 'form',
  state: {
    type: 'components',
    key: '',
    value: {},
    title: '',
    description: '',
    category: '',
    code: '',
    tags: [],
    state: '',
    props: {},
    settings: {
      gridOptions: {
        w: 2,
        h: 1
      }
    },
    interactivity: [],
    dataTypes: []
  },
  ModalHeader: {
    title: 'Add a new component',
    p: 'Fill initial settings and choose a template'
  },
  Flex: {
    overflow: 'auto',
    padding: 'A C3 0',
    minHeight: 'G3',
    minWidth: '100%',
    flow: 'column',
    flexAlign: 'flex-start space-between',
    gap: 'C1',
    'Flex.info': {
      width: '100%',
      flow: 'column',
      gap: 'B3',
      childProps: {
        width: '50%'
      },
      // The shared field kit (components/AppField.js): the caption label
      // above the control, the brand `field` fill with the hairline edge.
      // edit-component-modal.js extends this page and disables this field
      // BY KEY (`'AppField.key'` → `AppInput: { disabled }`), so the two
      // pages move together.
      'AppField.key': {
        label: (el) => el.call('polyglot', 'editor.addComponent.key'),
        AppInput: {
          required: true,
          value: '{{ key }}',
          placeholder: 'CardSidebar',
          onKeydown: (e, el) => {
            const val = el.node.value
            const preventChars = el.call('stringIncludesAny', val, [
              '&',
              '*',
              '-',
              ':',
              '@',
              '.',
              '/',
              '!',
              ' '
            ])
            if (preventChars) {
              e.preventDefault()
              return false
            }
            return true
          },
          onKeyup: (e, el, s) => {
            const val = el.node.value
            s.update({
              touched: true,
              key: val.replace(/[^a-zA-Z]/gu, '')
            })
          }
        }
      },
      'GroupField.draft': {
        margin: 'A - -',
        align: 'start',
        Title: {
          text: '{{ editor.addComponent.isDraft | polyglot }}'
        },
        SwitchFieldWithCaption: {
          align: 'start start',
          onChange: (ev, el, s) => {
            s.update({
              draft: !s.draft
            })
          },
          Input: {
            checked: ({ state }) => state.draft
          },
          SwitchField: {},
          Caption: {
            padding: '0',
            opacity: '.65',
            color: 'gray7',
            text: '{{ editor.addComponent.draft | polyglot }}'
          }
        }
      }
    },
    'Button.showAdvanced': {
      margin: '- -Z2',
      gap: 'X',
      background: 'transparent',
      borderWidth: 'hairline',
      borderStyle: 'solid',
      theme: 'common-card-outline-interactive',
      icon: (el, s) => `arrow angle ${s.advanced ? 'up' : 'down'}`,
      text: (el, s) =>
        el.call('polyglot', s.advanced ? 'editor.advanced.hide' : 'editor.advanced.show'),
      onClick: (ev, el, s) =>
        s.update({
          advanced: !s.advanced
        })
    },
    'Flex.advanced': {
      hide: ({ state }) => !state.advanced,
      flow: 'column',
      align: 'flex-start space-between',
      gap: 'C1',
      width: '100%',
      // The text fields here are the shared field kit (components/AppField.js):
      // the caption label above the control, the brand `field` fill with the
      // hairline edge. The controls carry behaviour only.
      'AppField.title': {
        label: (el) => el.call('polyglot', 'editor.addComponent.title'),
        AppInput: {
          value: '{{ title }}',
          placeholder: 'Card with Sidebar',
          autofocus: true,
          onKeyup: (e, el, s) => {
            const val = el.node.value
            const canApplyKey = !s.touched && window.location.pathname.includes('/add-component')
            const obj = {
              title: val
            }
            if (canApplyKey) {
              obj.key = el.call('toTitleCase', val).replace(/[^a-zA-Z]/gu, '')
            }
            s.update(obj)
          }
        }
      },
      'GroupField.value': {
        minWidth: '100%',
        Title: {
          text: '{{ editor.addComponent.initialCode | polyglot }}'
        },
        CodePreviewWidget: {
          props: (el, s) => ({
            minWidth: '100%',
            widthRange: null,
            theme: 'field-static',
            round: 'X2',
            padding: 'X2 X2 X2 -',
            minHeight: 'G1',
            Monaco: {
              foldLevel: false,
              opacity: 1,
              fileTab: {
                code: s.code
                  ? el.call('decodeNewlines', s.code)
                  : s.value
                    ? el.stringifyCode(s.value)
                    : 'export default {}',
                type: 'javascript',
                filename: 'value.js',
                fileTabKey: 'value'
              },
              onCodeEditCallback: async (editor) => {
                const code = editor.getValue()
                const value = await el.call('parseStringCode', /* N5: parse the buffer, never eval it */ code)
                s.quietReplace({
                  code: el.call('encodeNewlines', code),
                  value
                })
              }
            }
          })
        }
      },
      'AppField.description': {
        width: '100%',
        label: (el) => el.call('polyglot', 'editor.addComponent.description'),
        AppTextarea: {
          placeholder: (el) => el.call('polyglot', 'editor.addComponent.descriptionPlaceholder'),
          text: '{{ description }}',
          onKeyup: (e, el, s) => {
            s.update({
              description: el.node.value
            })
          }
        }
      },
      // The comma hint is the kit's `hint` line, under the control.
      'AppField.tags': {
        width: '50%',
        label: (el) => el.call('polyglot', 'editor.addComponent.tags'),
        hint: (el) => el.call('polyglot', 'editor.addComponent.tagsHint'),
        AppInput: {
          placeholder: 'Landing, Survey',
          value: (el, s) => (el.call('isArray', s.tags) ? s.tags.join(', ') : s),
          onKeyup: (e, el, s) => {
            s.tags = el.node.value.split(',').map((v) => v.trim())
            s.update({
              tags: s.tags
            })
          }
        }
      },
      'AppField.figmaUrl': {
        width: '50%',
        label: (el) => el.call('polyglot', 'editor.addComponent.figmaUrl'),
        AppInput: {
          value: '{{ figmaUrl }}',
          placeholder: 'https://figma.com/...',
          onChange: (e, el, s) => {
            const { value } = el.node
            s.update({
              figmaUrl: value
            })
          }
        }
      },
      'AppField.stateStr': {
        if: (el, s, ctx) => s.stateStr || (s.state && ctx.utils.isString(s.state)),
        width: '50%',
        label: (el) => el.call('polyglot', 'editor.addComponent.statePath'),
        AppInput: {
          placeholder: 'about/team',
          value: (el, s) => s.state,
          onKeyup: (e, el, s) =>
            s.update({
              state: el.node.value
            })
        }
      },
      'Group.state': {
        if: (el, s, ctx) => !s.stateStr || !s.state || (s.state && !ctx.utils.isString(s.state)),
        props: {
          minWidth: '100%'
        },
        Title: {
          text: '{{ editor.addComponent.mockupState | polyglot }}'
        },
        CodePreviewWidget: {
          props: (el, s) => ({
            minWidth: '100%',
            widthRange: null,
            theme: 'field-static',
            round: 'X2',
            padding: 'X2 X2 X2 -',
            minHeight: 'G1',
            Monaco: {
              foldLevel: false,
              opacity: 1,
              fileTab: {
                code: s.state ? el.stringifyCode(s.state, el, s) : 'export default {}',
                type: 'javascript',
                filename: 'state.js',
                fileTabKey: 'state'
              },
              onCodeEditCallback: async (editor) => {
                const state = await el.call('parseStringCode', /* N5: parse the buffer, never eval it */ editor.getValue())
                s.quietReplace({
                  state
                })
              }
            }
          })
        }
      },
      'SurveyIconicOptions.interactivity': {
        multiple: true,
        width: '100%',
        lowercase: true,
        margin: '0',
        Title: {
          text: '{{ editor.addComponent.interactivityTypes | polyglot }}'
        },
        options: {
          'Option.Hover': {
            icon: 'hover outline'
          },
          'Option.Click': {
            icon: 'click outline'
          },
          'Option.Choose': {
            icon: 'choose outline'
          },
          'Option.Drag': {
            icon: 'drag outline'
          },
          'Option.Input': {
            icon: 'input outline'
          },
          'Option.Focus': {
            icon: 'focus outline'
          },
          Other: null
        }
      },
      'SurveyIconicOptions.dataTypes': {
        multiple: true,
        width: '100%',
        lowercase: true,
        margin: '0',
        Title: {
          text: '{{ editor.addComponent.valueTypes | polyglot }}'
        },
        options: {
          'Option.Boolean': {
            icon: 'boolean outline'
          },
          'Option.Int': {
            icon: 'int outline'
          },
          'Option.String': {
            icon: 'string outline'
          },
          'Option.Object': {
            icon: 'object outline'
          },
          'Option.Array': {
            icon: 'array outline'
          },
          'Option.Date': {
            icon: 'date outline'
          },
          Other: null
        }
      },
      'Group.props': {
        props: {
          minWidth: '100%'
        },
        Title: {
          text: '{{ editor.addComponent.frameProperties | polyglot }}'
        },
        CodePreviewWidget: {
          minWidth: '100%',
          widthRange: null,
          theme: 'field-static',
          round: 'X2',
          padding: 'X2 X2 X2 -',
          minHeight: 'G1',
          props: (el, s) => ({
            Monaco: {
              foldLevel: false,
              opacity: 1,
              fileTab: {
                code: el.stringifyCode(s.props),
                type: 'javascript',
                filename: 'props.js',
                fileTabKey: 'props'
              },
              onCodeEditCallback: async (editor) => {
                const props = await el.call('parseStringCode', /* N5: parse the buffer, never eval it */ editor.getValue())
                if (props?.[s.key]) {
                  props.demoComponent = props[s.key]
                }
                s.quietReplace({
                  props
                })
              }
            }
          })
        }
      }
    }
  },
  ModalFooter: {
    CheckButton: {
      type: 'submit',
      reverse: true,
      icon: 'arrow right',
      text: '{{ common.action.next | polyglot }}'
    }
  }
}
